<template>
  <div class="employee-editor">
    <AppMapPointPanelHeader :header="header" @close="$emit('close')" v-if="hasHeader" />
    <div :class="['employee-editor__content', { 'employee-editor__content_padding-off': paddingOff }]">
      <b-form :disabled="!canSave">
        <slot name="floor"></slot>
        <b-form-group label="Employee photo" v-if="canSave || (scimEmployeesSyncEnabled && !isNew)">
          <div :class="{ 'with-photo': employeeTemp.avatar_id }">
            <AppAvatar v-if="employeeTemp.avatar_id" :src="avatarSrc" :size="avatarSize" />
            <AppFileUpload
              ref="uploader"
              :post-url="avatarUrl"
              auto-upload
              :short-view="Boolean(employeeTemp.avatar_id)"
              accept="image/png,image/gif,image/jpeg,image/webp"
              :size="1024 * 1024 * 2"
              :extensions="allowedExtensions"
              :short-view-message="userPhotoTitle"
              @upload="onUpload"
              @error="onUploadError"
            />
          </div>
        </b-form-group>
        <b-form-group label="Name*" :state="$v.employeeTemp.name.required" :disabled="!canSave">
          <b-input type="text" placeholder="Name" v-model="$v.employeeTemp.name.$model" />
          <template #invalid-feedback v-if="$v.employeeTemp.name.$dirty && !$v.employeeTemp.name.required">
            Employee name required
          </template>
        </b-form-group>
        <b-form-group label="Desk Number" v-if="canCreateInfinityEvent">
          <b-input type="text" placeholder="Desk Number" v-model="deskNumber" :disabled="scimDeskFieldMappingEnabled" />
        </b-form-group>
        <b-form-group label="Email address" :disabled="!canSave" :state="!$v.employeeTemp.email.$error">
          <b-input type="email" placeholder="Email" v-model="$v.employeeTemp.email.$model" />
          <template #invalid-feedback v-if="$v.employeeTemp.email.$error">
            Email required to create user
          </template>
        </b-form-group>
        <b-form-group label="Phone" :disabled="!canSave">
          <b-input type="text" v-model="$v.employeeTemp.phone.$model" />
        </b-form-group>
        <b-form-group label="Cell phone" :disabled="!canSave">
          <b-input type="text" v-model="$v.employeeTemp.cell.$model" />
        </b-form-group>
        <b-form-group label="Department" :disabled="!canSave" :state="!employeeTemp.department_id && $v.departmentTemp.name.requiredIf">
          <b-input-group v-if="employeeTemp.department_id">
            <b-input v-model="$v.departmentTemp.name.$model" />
            <template #append>
              <b-btn @click="departmentClose"><i class="ion ion-md-close"></i></b-btn>
            </template>
          </b-input-group>
          <AppSelect
            v-else
            @search="departmentSearch"
            @select="departmentSelect"
            ref="departmentSelect"
            :proposeNew="can(PN.DEPARTMENT_CREATE)"
            :items="departmentsByTerm"
            placeholder="select department"
          >
            <template #default="props">
              <span class="select-item">
                <span class="select-item-text">{{ props.item.name }}</span>
                <b-btn size="sm" class="align-self-center" @click.stop.prevent="departmentDestroy_(props.item.id)">
                  <i class="ion ion-md-trash"></i
                ></b-btn>
              </span>
            </template>
          </AppSelect>
          <template #invalid-feedback v-if="employeeTemp.department_id && !$v.departmentTemp.name.requiredIf">
            Department name required
          </template>
        </b-form-group>
        <b-form-group :disabled="!canSave" label="Position" :state="!employeeTemp.position_id && $v.positionTemp.name.requiredIf">
          <b-input-group v-if="employeeTemp.position_id">
            <b-input v-model="$v.positionTemp.name.$model" />
            <template #append>
              <b-btn class="align-self-center" @click="positionClose"><i class="ion ion-md-close"></i></b-btn>
            </template>
          </b-input-group>
          <app-select
            v-else
            @search="positionSearch"
            @select="positionSelect"
            ref="positionSelect"
            :proposeNew="can(PN.EMPLOYEE_POSITION_CREATE)"
            :items="positionsByTerm"
            placeholder="select position"
          >
            <template #default="props">
              <span class="select-item">
                <span class="select-item-text">{{ props.item.name }}</span>
                <b-btn size="sm" class="align-self-center" @click.stop.prevent="positionDestroy_(props.item.id)"
                  ><i class="ion ion-md-trash"></i
                ></b-btn>
              </span>
            </template>
          </app-select>
          <template #invalid-feedback v-if="employeeTemp.position_id && !$v.positionTemp.name.requiredIf">
            Position name required
          </template>
        </b-form-group>
        <b-form-group label="Employee ID" :disabled="!canSave">
          <b-input type="text" v-model="$v.employeeTemp.hrid.$model" />
        </b-form-group>
        <b-form-group label="Note" :disabled="!canSave">
          <b-textarea placeholder="add some note..." v-model="$v.employeeTemp.note.$model" />
        </b-form-group>
        <div class="form-check" v-if="canCreateRelatedUser">
          <input class="form-check-input" type="checkbox" id="createUser" v-model="createRelatedUser" />
          <label class="form-check-label" for="createUser">create related user</label>
        </div>
        <slot name="extend" :canSave="canSave"></slot>
        <div v-if="errors" class="mb-3">
          <div class="text-danger" v-for="(error, i) in errors" :key="i">{{ error }}</div>
        </div>
        <div v-if="canPlaceOnMap && user.booking_enabled && point && point.id && !isNew">
          <a href="#" class="release-link" @click.prevent="releaseDesk">Remove employee currently assigned to this desk</a>
        </div>
        <div class="btn-panel my-2">
          <b-btn v-if="canDelete" type="button" variant="default" @click="showDeleteModal = true" title="Delete employee" class="mr-auto"
            ><i class="ion ion-md-trash"></i
          ></b-btn>
          <div class="ml-auto"></div>
          <b-btn type="button" variant="primary" @click="save">Save</b-btn>
          <b-btn type="button" variant="default" @click="cancel">Cancel</b-btn>
        </div>
        <EmployeeDeleteModal v-if="showDeleteModal" :employee="employee" :on-confirm="handleDelete" :has-user-delete="hasEmail" @close="showDeleteModal = false" />
      </b-form>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import nanoid from 'nanoid'
import AppSelect from '@/components/shared/AppSelect'
import AppFileUpload from '@/components/shared/AppFileUpload'
import Id, { isLocalId } from '@/utils/Id'
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import * as PN from '@/utils/PermissionNames'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { getAvatarSrc } from '@/utils/Helpers'
import { DEFAULT_POINT_LAT_LON, POINT_DESTROY, POINT_ICONS, POINT_UPDATE } from '@/store/modules/point'
import AppAvatar from '@/components/shared/AppAvatar'
import { SERVER_DATE_TIME_FORMAT } from '@/components/desk/utils'
import { put } from '@/utils/Api'
import UrlStore from '@/utils/UrlStore'
import { EMPLOYEE_UPDATE } from '@/store/modules/employee'
import { getItemHeader } from '@/components/shared/helpers'
import AppMapPointPanelHeader from '@/components/shared/AppMapPointPanelHeader.vue'
import EmployeeDeleteModal from '@/components/employee/EmployeeDeleteModal.vue'
import escape from 'lodash/escape'
import { canManageEmployeeDeskEvents } from '@/utils/DeskEventOwnership'

export default {
  name: 'EmployeeEditor',
  components: {
    AppMapPointPanelHeader,
    AppAvatar,
    AppSelect,
    AppFileUpload,
    EmployeeDeleteModal
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    hasHeader: {
      type: Boolean,
      default: true
    },
    paddingOff: {
      type: Boolean,
      default: false
    },
    point: {
      type: Object
    }
  },
  validations() {
    return {
      employeeTemp: {
        name: {
          required
        },
        email: {
          requiredIf: requiredIf(() => this.handleUser)
        },
        phone: {},
        cell: {},
        department_id: {},
        position_id: {},
        avatar_id: {},
        is_available: {},
        note: {},
        hrid: {}
      },
      departmentTemp: {
        name: {
          requiredIf: requiredIf(() => this.employeeTemp.department_id)
        }
      },
      positionTemp: {
        name: {
          requiredIf: requiredIf(() => this.employeeTemp.position_id)
        }
      }
    }
  },
  data: () => ({
    PN,
    deskNumber: '',
    idsToSync: {
      deskId: null, // preserve for event sync
      deskPointId: null, // preserve for desk sync
      employeeId: null
    },
    employeeTemp: {
      name: '',
      email: '',
      phone: '',
      hrid: '',
      note: '',
      cell: '',
      department_id: '',
      position_id: '',
      avatar_id: '',
      avatar_updated_at: '',
      is_available: false
    },
    departmentTemp: {
      name: ''
    },
    positionTemp: {
      name: ''
    },
    positionsByTerm: [],
    departmentsByTerm: [],
    allowedExtensions: 'gif,jpg,jpeg,png,webp',
    errors: [],
    showDeleteModal: false,
    createRelatedUser: false
  }),
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('rbac', ['can', 'canBookForOthers']),
    ...mapGetters('employee', ['getEmployee']),
    ...mapGetters('position', ['positions', 'getPosition']),
    ...mapGetters('department', ['departments', 'getDepartment']),
    ...mapGetters('desk', ['getDesk']),
    ...mapGetters('floor', ['floors']),
    ...mapGetters('settings', ['scimEmployeesSyncEnabled', 'scimDeskFieldMappingEnabled', 'scimUsersSyncEnabled']),
    canCreateRelatedUser() {
      return this.isNew && this.canSave && this.can(PN.USER_CRUD) && !this.scimUsersSyncEnabled
    },
    handleUser() {
      return this.canCreateRelatedUser && this.createRelatedUser
    },
    hasEmail() {
      return !!this.employee.email
    },
    canPlaceOnMap() {
      return canManageEmployeeDeskEvents(this.employeeTemp, this.user, this.canBookForOthers)
    },
    canCreateInfinityEvent() {
      return this.can(PN.DESK_EVENT_CREATE_INFINITY)
    },
    canCreate() {
      return this.can(PN.EMPLOYEE_CREATE) && !this.scimEmployeesSyncEnabled
    },
    canUpdate() {
      return this.can(PN.EMPLOYEE_UPDATE) && !(this.scimEmployeesSyncEnabled && this.employee.scim_id)
    },
    canDelete() {
      return this.can(PN.EMPLOYEE_DELETE) && !this.isNew && !(this.scimEmployeesSyncEnabled && this.employee.scim_id)
    },
    canSave() {
      return this.isNew ? this.canCreate : this.canUpdate
    },
    desk() {
      return this.point ? this.getDesk(this.point.desk) : null
    },
    header() {
      return getItemHeader(this.point, 'Employee')
    },
    avatarSize() {
      return this.employeeTemp.avatar_id ? '100px' : '50px'
    },
    avatarSrc() {
      return getAvatarSrc(this.employeeTemp.avatar_id, this.employeeTemp.avatar_updated_at)
    },
    avatarUrl() {
      const suffix = this.employeeTemp.avatar_id ? `/${this.employeeTemp.avatar_id}` : ''
      return `/api/v1/avatars${suffix}`
    },
    department() {
      return this.getDepartment(this.employeeTemp.department_id) || {}
    },
    employee() {
      return this.getEmployee(this.id)
    },
    isNew() {
      return isLocalId(this.employeeTemp.id)
    },
    position() {
      return this.getPosition(this.employeeTemp.position_id) || {}
    },
    userPhotoTitle() {
      return this.employeeTemp.avatar_id ? 'click to change user photo' : 'click to upload user photo'
    }
  },
  methods: {
    ...mapActions('employee', ['employeeSave', 'employeeDestroy']),
    ...mapActions('desk', ['deskSave', 'deskEventSave']),
    ...mapActions('position', ['positionSave', 'positionDestroy']),
    ...mapActions('department', ['departmentSave', 'departmentDestroy']),
    ...mapActions('point', ['pointSave', 'pointDestroy', 'pointLoad']),
    ...mapMutations('point', {
      pointUpdate: POINT_UPDATE,
      pointRemove: POINT_DESTROY,
    }),
    ...mapMutations('employee', {
      employeeUpdate: EMPLOYEE_UPDATE
    }),
    departmentClose() {
      this.employeeTemp.department_id = null
      this.departmentsByTerm = []
    },
    positionClose() {
      this.employeeTemp.position_id = null
      this.positionsByTerm = []
    },
    departmentSelect(p) {
      if (typeof p === 'object') {
        this.employeeTemp.department_id = p.id
      } else if (typeof p === 'string') {
        // is a new
        this.departmentSave({ id: Id(), name: p }).then((pId) => {
          this.employeeTemp.department_id = pId
        })
      } else {
        throw new Error("Can't resolve returned company type")
      }
    },
    departmentSearch(term) {
      this.departmentsByTerm = []
      this.$nextTick(() => {
        this.departmentsByTerm = term
          ? this.departments.filter((department) => department.name?.toLowerCase().includes(term.toLowerCase()))
          : this.departments
      })
    },
    departmentDestroy_(id) {
      const department = this.getDepartment(id)
      this.$appToast.question(`A you sure want to delete '${escape(department.name)}' department?`).then(() =>
        this.departmentDestroy(department).then(() => {
          this.$refs.departmentSelect.search('')
        }))
    },
    positionDestroy_(id) {
      const position = this.getPosition(id)
      this.$appToast.question(`A you sure want to delete '${escape(position.name)}' employee position?`).then(() =>
        this.positionDestroy(position).then(() => {
          this.$refs.positionSelect.search('')
        }))
    },
    positionSelect(p) {
      if (typeof p === 'object') {
        this.employeeTemp.position_id = p.id
      } else if (typeof p === 'string') {
        // is a new
        this.positionSave({ id: Id(), name: p }).then((pId) => {
          this.employeeTemp.position_id = pId
        })
      } else {
        throw new Error("Can't resolve returned company type")
      }
    },
    positionSearch(term) {
      this.positionsByTerm = []
      this.$nextTick(() => {
        this.positionsByTerm = term
          ? this.positions.filter((position) => position.name?.toLowerCase().includes(term.toLowerCase()))
          : this.positions
      })
    },
    onServerError(error) {
      if (!error?.response) {
        throw error
      }
      let newErrors = []
      if (error.response.data.errors) {
        newErrors = Object.values(error.response.data.errors).flat()
      } else if (Array.isArray(error.response.data)) {
        newErrors = error.response.data.map((e) => e.message)
      }
      this.errors.push(...newErrors)
    },
    async syncEmployee() {
      const [departmentId, positionId] = await Promise.all([
        this.employeeTemp.department_id ? this.departmentSave(this.departmentTemp).catch(this.onServerError) : Promise.resolve(null),
        this.employeeTemp.position_id ? this.positionSave(this.positionTemp).catch(this.onServerError) : Promise.resolve(null)
      ])

      this.employeeTemp.department_id = departmentId
      this.employeeTemp.position_id = positionId
      const employeeId = await this.employeeSave({
        employee: { ...this.employeeTemp, ...(this.handleUser && { handleUser: true }) },
        config: { params: { expand: 'point' } }
      }).catch(this.onServerError)
      this.idsToSync.employeeId = employeeId ?? this.idsToSync.employeeId
    },
    async syncPoint() {
      const deskPointId = await this.pointSave({
        icon_id: POINT_ICONS.EMPLOYEE,
        lat_lon: DEFAULT_POINT_LAT_LON,
        floor_id: this.floors[0]?.id ?? null,
        ...(this.point ?? {}),
        id: this.idsToSync.deskPointId ?? Id(),
        title: undefined
      })
      this.idsToSync.deskPointId = deskPointId ?? this.idsToSync.deskPointId
    },
    async syncDesk() {
      const deskId = await this.deskSave({
        desk: {
          id: this.idsToSync.deskId || Id(),
          point_id: this.idsToSync.deskPointId,
          number: this.deskNumber
        }
      }).catch(this.onServerError)

      this.idsToSync.deskId = deskId ?? this.idsToSync.deskId
    },
    async createInfinityDeskEvent() {
      await this.deskEventSave({
        deskEvent: {
          id: Id(),
          start: dayjs().format(SERVER_DATE_TIME_FORMAT),
          end: 'infinity',
          employee_id: this.idsToSync.employeeId,
          desk_id: this.idsToSync.deskId
        }
      }).catch(this.onServerError)
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }
      this.errors = []
      const pointSyncRequired = !!this.point || (this.deskNumber && this.deskNumber !== this.point?.desk?.number)
      const createInfinityDeskEventRequired =
        this.canCreateInfinityEvent && ((this.point && isLocalId(this.point.id)) || (this.deskNumber && !this.desk))
      if (pointSyncRequired) {
        await this.syncPoint()
        await this.syncDesk()
      }

      if (this.canSave && !this.errors.length) {
        await this.syncEmployee()
      }

      if (createInfinityDeskEventRequired && !this.errors.length) {
        await this.createInfinityDeskEvent()
      }

      if (!this.errors.length) {
        // we have to save employee again to update point
        const { employeeId, deskId } = this.idsToSync
        this.canSave && (await this.employeeSave({ employee: { id: employeeId } }))
        const pointId = this.idsToSync.deskPointId
        if (pointId) {
          this.pointLoad({ id: pointId, params: { expand: 'employee' } })
        }
        this.$emit('save', { pointId, employeeId, deskId })
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    async handlePointOnEmployeeDelete() {
      if (!this.point) {
        return
      }

      if (this.user.booking_enabled && this.point.id) {
        try {
          await this.pointLoad({ id: this.point.id })
        } catch (e) {
          if (e.response?.status === 404) {
            this.pointRemove(this.point)
            return
          }
          throw e
        }
      }
    },
    async handleDelete({ deleteRelatedUser, deleteInfinityDesks }) {
      try {
        const params = {
          handleUser: deleteRelatedUser ? 'true' : 'false',
          handleDesks: deleteInfinityDesks ? 'true' : 'false'
        }
        await this.employeeDestroy({ employee: this.employee, config: { params } })
        await this.handlePointOnEmployeeDelete()
        this.showDeleteModal = false
        this.$emit('destroy')
      } catch (e) {
        this.onServerError(e)
      }
    },
    async releaseDesk() {
      const r = await put(`${UrlStore.EMPLOYEES}/${this.employeeTemp.id}/release`)
      if (!r.data) {
        return
      }
      if (r.data.point) {
        r.data.point.employee = null // fix title bug
        this.pointUpdate(r.data.point)
      }
      if (r.data.employee) {
        this.employeeUpdate(r.data.employee)
      }
    },
    onUpload(avatar) {
      this.employeeTemp.avatar_id = avatar.id
      this.employeeTemp.avatar_updated_at = nanoid()

      // when SCIM sync is enabled we can't save the whole employee record,
      // but avatar updates should still go through our dedicated endpoint.
      // also don't attempt for a new (unsaved) employee.
      if (!this.isNew && this.scimEmployeesSyncEnabled) {
        // call backend path that ignores SCIM restrictions
        put(`${UrlStore.EMPLOYEES}/${this.employee.id}/avatar`, { avatar_id: avatar.id })
          .then((r) => {
            // update store with new employee data if returned
            if (r && r.data) {
              this.$store.commit(EMPLOYEE_UPDATE, r.data)
            }
            this.$appToast.success('User photo uploaded!')
          })
          .catch(this.onServerError)
      } else {
        this.$appToast.success('User photo uploaded!')
      }
    },
    onUploadError({ cause, response }) {
      let errorMessages = []
      if (cause === 'denied' && response.length) {
        response.forEach((error) => {
          errorMessages.push(error.message)
        })
      } else if (cause === 'size') {
        errorMessages.push('File is too big. Its size cannot exceed 2 MiB.')
      } else if (cause === 'extension') {
        errorMessages.push(`File extension is not allowed. Support ${this.allowedExtensions}.`)
      } else {
        errorMessages.push(`Unknown error on file upload. Please write to support.`)
      }
      this.$appToast.error(errorMessages.map(escape).join('<br>'))
    }
  },
  watch: {
    id: {
      immediate: true,
      handler(id) {
        this.employeeTemp = Object.assign({ id }, this.employeeTemp, this.getEmployee(id))
        this.deskNumber = this.desk?.number
        this.idsToSync.deskPointId = this.point?.id
        this.idsToSync.deskId = this.desk?.id
        this.idsToSync.employeeId = id
      }
    },
    department: {
      immediate: true,
      handler(value) {
        this.departmentTemp = Object.assign({}, this.departmentTemp, value)
      }
    },
    position: {
      immediate: true,
      handler(value) {
        this.positionTemp = Object.assign({}, this.positionTemp, value)
      }
    }
  }
}
</script>

<style lang="scss">
.employee-editor__content {
  padding: 24px;
  overflow: clip;
  &_padding-off {
    padding: 0;
  }
}

.select-item {
  display: flex;
  justify-items: left;

  .btn {
    margin-left: auto;
  }
}

.with-photo {
  display: flex;
  align-items: center;

  & .file-upload {
    margin-left: 20px;
    max-width: 7em;
  }
}

.release-link {
  margin: 8px 0 8px auto;
  display: inline-block;
  white-space: nowrap;
  border-bottom: 1px dashed #1e70cd;
  line-height: 14px;
}
</style>
